import React, { useState, useEffect } from 'react';
import { MapPin, Package, Clock, Phone, Navigation, AlertCircle, CheckCircle, Truck, User } from 'lucide-react';
import RoleBasedHeader from 'components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';


const LiveShipmentTracking = () => {
  const [selectedShipment, setSelectedShipment] = useState('SH-2025-001');
  const [trackingData, setTrackingData] = useState({});

  // Mock tracking data for all shipments
  const shipmentsTrackingData = {
    'SH-2025-001': {
      orderNumber: 'ORD-2025-12345',
      shipmentId: 'SH-2025-001',
      status: 'In Transit',
      currentLocation: { lat: 23.0225, lng: 72.5714, address: 'Ahmedabad Ring Road, Gujarat' },
      pickup: { address: 'ABC Sugar Mill, Gandhinagar, Gujarat', time: '2025-12-01 08:00 AM', completed: true },
      delivery: { address: 'XYZ Warehouse, Mumbai, Maharashtra', time: 'Expected: 2025-12-01 06:00 PM', completed: false },
      driver: { name: 'Rajesh Kumar', phone: '+91 98765-43210', vehicleNumber: 'GJ-01-AB-1234' },
      milestones: [
        { id: 1, title: 'Order Confirmed', time: '2025-12-01 07:30 AM', status: 'completed', icon: CheckCircle },
        { id: 2, title: 'Pickup Completed', time: '2025-12-01 08:15 AM', status: 'completed', icon: CheckCircle },
        { id: 3, title: 'En Route to Delivery', time: '2025-12-01 09:00 AM', status: 'active', icon: Truck },
        { id: 4, title: 'Delivery in Progress', time: 'Pending', status: 'pending', icon: Package },
        { id: 5, title: 'POD Uploaded', time: 'Pending', status: 'pending', icon: CheckCircle }
      ],
      distance: { total: '450 km', covered: '285 km', remaining: '165 km' },
      eta: '2025-12-01 05:45 PM',
      lastUpdate: '2 minutes ago',
      speed: '65 km/h',
      progress: 63
    },
    'SH-2025-002': {
      orderNumber: 'ORD-2025-12346',
      shipmentId: 'SH-2025-002',
      status: 'Delivered',
      currentLocation: { lat: 18.5204, lng: 73.8567, address: 'Pune Delivery Hub, Maharashtra' },
      pickup: { address: 'DEF Sugar Mill, Nashik, Maharashtra', time: '2025-11-30 09:00 AM', completed: true },
      delivery: { address: 'PQR Warehouse, Pune, Maharashtra', time: '2025-11-30 04:30 PM', completed: true },
      driver: { name: 'Suresh Patil', phone: '+91 98123-45678', vehicleNumber: 'MH-12-CD-5678' },
      milestones: [
        { id: 1, title: 'Order Confirmed', time: '2025-11-30 08:30 AM', status: 'completed', icon: CheckCircle },
        { id: 2, title: 'Pickup Completed', time: '2025-11-30 09:15 AM', status: 'completed', icon: CheckCircle },
        { id: 3, title: 'En Route to Delivery', time: '2025-11-30 10:00 AM', status: 'completed', icon: Truck },
        { id: 4, title: 'Delivery in Progress', time: '2025-11-30 04:00 PM', status: 'completed', icon: Package },
        { id: 5, title: 'POD Uploaded', time: '2025-11-30 04:45 PM', status: 'completed', icon: CheckCircle }
      ],
      distance: { total: '220 km', covered: '220 km', remaining: '0 km' },
      eta: 'Delivered',
      lastUpdate: '1 day ago',
      speed: '0 km/h',
      progress: 100
    },
    'SH-2025-003': {
      orderNumber: 'ORD-2025-12347',
      shipmentId: 'SH-2025-003',
      status: 'Pickup Scheduled',
      currentLocation: { lat: 21.1702, lng: 72.8311, address: 'GHI Sugar Mill, Surat, Gujarat' },
      pickup: { address: 'GHI Sugar Mill, Surat, Gujarat', time: 'Scheduled: 2025-12-02 07:00 AM', completed: false },
      delivery: { address: 'LMN Warehouse, Surat City, Gujarat', time: 'Expected: 2025-12-02 11:00 AM', completed: false },
      driver: { name: 'Amit Shah', phone: '+91 98765-12345', vehicleNumber: 'GJ-06-EF-9012' },
      milestones: [
        { id: 1, title: 'Order Confirmed', time: '2025-12-01 06:00 PM', status: 'completed', icon: CheckCircle },
        { id: 2, title: 'Pickup Scheduled', time: '2025-12-02 07:00 AM', status: 'active', icon: Clock },
        { id: 3, title: 'En Route to Delivery', time: 'Pending', status: 'pending', icon: Truck },
        { id: 4, title: 'Delivery in Progress', time: 'Pending', status: 'pending', icon: Package },
        { id: 5, title: 'POD Uploaded', time: 'Pending', status: 'pending', icon: CheckCircle }
      ],
      distance: { total: '35 km', covered: '0 km', remaining: '35 km' },
      eta: '2025-12-02 11:00 AM',
      lastUpdate: '10 minutes ago',
      speed: '0 km/h',
      progress: 0
    }
  };

  const shipments = [
    { id: 'SH-2025-001', order: 'ORD-2025-12345', status: 'In Transit', destination: 'Mumbai' },
    { id: 'SH-2025-002', order: 'ORD-2025-12346', status: 'Delivered', destination: 'Pune' },
    { id: 'SH-2025-003', order: 'ORD-2025-12347', status: 'Pickup Scheduled', destination: 'Surat' }
  ];

  // Update tracking data when selected shipment changes
  useEffect(() => {
    const selectedData = shipmentsTrackingData?.[selectedShipment];
    if (selectedData) {
      setTrackingData(selectedData);
    }
  }, [selectedShipment]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'In Transit': return 'text-blue-600 bg-blue-50';
      case 'Delivered': return 'text-green-600 bg-green-50';
      case 'Pickup Scheduled': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  useEffect(() => {
    // Simulate real-time updates only for active shipments
    if (trackingData?.status === 'In Transit') {
      const interval = setInterval(() => {
        setTrackingData(prev => ({
          ...prev,
          lastUpdate: 'Just now',
          currentLocation: {
            ...prev?.currentLocation,
            lat: prev?.currentLocation?.lat + (Math.random() - 0.5) * 0.01,
            lng: prev?.currentLocation?.lng + (Math.random() - 0.5) * 0.01
          }
        }));
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [trackingData?.status]);

  return (
    <div className="min-h-screen" style={{ background: '#f8f1e9' }}>
      <RoleBasedHeader />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Live Shipment Tracking</h1>
          <p className="text-gray-600">Real-time GPS tracking and delivery monitoring</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shipment List Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Shipments</h2>
              <div className="space-y-3">
                {shipments?.map(shipment => (
                  <button
                    key={shipment?.id}
                    onClick={() => setSelectedShipment(shipment?.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedShipment === shipment?.id 
                        ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{shipment?.id}</p>
                        <p className="text-sm text-gray-600">{shipment?.order}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getShipmentStatusColor(shipment?.status)}`}>
                        {shipment?.status}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>{shipment?.destination}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Tracking Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipment Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{trackingData?.shipmentId}</h2>
                  <p className="text-gray-600">{trackingData?.orderNumber}</p>
                </div>
                <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-semibold">
                  {trackingData?.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Pickup Location</p>
                    <p className="text-gray-900">{trackingData?.pickup?.address}</p>
                    <p className="text-sm text-green-600 flex items-center mt-1">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {trackingData?.pickup?.time}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Delivery Location</p>
                    <p className="text-gray-900">{trackingData?.delivery?.address}</p>
                    <p className="text-sm text-orange-600 mt-1">
                      {trackingData?.delivery?.time}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder with Real-time Location */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 mb-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-600 rounded-full animate-ping"></div>
                  <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-green-600 rounded-full"></div>
                  <div className="absolute bottom-1/4 left-1/2 w-3 h-3 bg-blue-600 rounded-full pulse"></div>
                </div>
                <div className="text-center relative z-10">
                  <Navigation className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">GPS Tracking Active</h3>
                  <p className="text-gray-700 mb-2 font-medium">{trackingData?.currentLocation?.address}</p>
                  <p className="text-sm text-gray-600 mb-4">
                    Coordinates: {trackingData?.currentLocation?.lat?.toFixed(4)}, {trackingData?.currentLocation?.lng?.toFixed(4)}
                  </p>
                  <div className="flex items-center justify-center space-x-6 text-sm">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-blue-600" />
                      <span className="font-medium">Last Update: {trackingData?.lastUpdate}</span>
                    </div>
                    <div className="flex items-center">
                      <Truck className="w-4 h-4 mr-1 text-blue-600" />
                      <span className="font-medium">Speed: {trackingData?.speed}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Total Distance</p>
                        <p className="font-semibold text-gray-900">{trackingData?.distance?.total}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Covered</p>
                        <p className="font-semibold text-green-600">{trackingData?.distance?.covered}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Remaining</p>
                        <p className="font-semibold text-orange-600">{trackingData?.distance?.remaining}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ETA Information */}
              <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Estimated Time of Arrival</p>
                    <p className="text-xl font-bold text-gray-900">{trackingData?.eta}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Progress</p>
                  <p className="text-lg font-semibold text-blue-600">{trackingData?.progress}%</p>
                </div>
              </div>
            </div>

            {/* Delivery Milestones */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Delivery Milestones</h3>
              <div className="space-y-4">
                {trackingData?.milestones?.map((milestone, index) => {
                  const Icon = milestone?.icon;
                  return (
                    <div key={milestone?.id} className="flex items-start space-x-4">
                      <div className="relative">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          milestone?.status === 'completed' ? 'bg-green-100' :
                          milestone?.status === 'active' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${
                            milestone?.status === 'completed' ? 'text-green-600' :
                            milestone?.status === 'active' ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                        </div>
                        {index < trackingData?.milestones?.length - 1 && (
                          <div className={`absolute left-1/2 top-12 w-0.5 h-8 transform -translate-x-1/2 ${
                            milestone?.status === 'completed' ? 'bg-green-300' : 'bg-gray-300'
                          }`}></div>
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">{milestone?.title}</h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(milestone?.status)}`}>
                            {milestone?.status === 'completed' ? 'Completed' : 
                             milestone?.status === 'active' ? 'In Progress' : 'Pending'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{milestone?.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Communication Hub */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Communication Hub</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{trackingData?.driver?.name}</p>
                      <p className="text-sm text-gray-600">Driver</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-700">
                      <Phone className="w-4 h-4 mr-2 text-gray-500" />
                      <span>{trackingData?.driver?.phone}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Truck className="w-4 h-4 mr-2 text-gray-500" />
                      <span>{trackingData?.driver?.vehicleNumber}</span>
                    </div>
                  </div>
                  <button className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition flex items-center justify-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Driver
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Recent Notifications</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">Shipment picked up successfully</p>
                        <p className="text-xs text-gray-500">3 hours ago</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">Driver approaching delivery zone</p>
                        <p className="text-xs text-gray-500">5 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">ETA updated based on traffic</p>
                        <p className="text-xs text-gray-500">15 minutes ago</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveShipmentTracking;